import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { useCaseStudyById } from "@/lib/content";
import RichText from "@/components/RichText";

export default function CaseStudyDetail() {
  const { id } = useParams();
  const { data: c, loading } = useCaseStudyById(id);

  if (loading && !c) {
    return (
      <section className="bg-white pt-20 pb-24 min-h-[60vh] flex items-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-slate-400 text-sm">
          Loading…
        </div>
      </section>
    );
  }

  if (!c) {
    return (
      <section className="bg-white pt-20 pb-24 min-h-[60vh] flex items-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-serif text-4xl text-[#0a2540] mb-4">Case study not found</h1>
          <Link to="/results" className="inline-flex items-center gap-2 text-[#0a2540] link-underline">
            <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" /> Back to results
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO title={`${c.headline || c.title} — Johnny Tan`} description={c.challenge} />

      <section className="bg-white pt-20 pb-12 md:py-20" data-testid={`case-detail-${c.id}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/results" className="text-xs uppercase tracking-[0.25em] text-[#0a2540]/70 link-underline inline-flex items-center gap-2 mb-6">
            <i className="fa-solid fa-arrow-left text-[10px]" aria-hidden="true" /> Back to Proven Results
          </Link>
          <Reveal>
            {c.highlight && (
              <div className="inline-block bg-[#fffbea] text-[#0a2540] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest rounded-full mb-5">
                {c.highlight}
              </div>
            )}
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1] mb-3">
              {c.headline || c.title}
            </h1>
            {c.propertyType && (
              <p className="text-xs text-slate-500 uppercase tracking-widest">{c.propertyType}</p>
            )}
          </Reveal>
        </div>
      </section>

      {c.image && (
        <div className="bg-[#fafafa]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="aspect-[16/9] overflow-hidden bg-gray-100 rounded-2xl"
            >
              <img src={c.image} alt={c.headline || c.title} className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      )}

      <section className="bg-white py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <RichText html={c.story} className="text-base text-slate-700 leading-relaxed" />
        </div>
      </section>

      <CTABanner />
    </>
  );
}
